/* eslint-disable max-lines */
import {
    useContext, useState
} from 'react';
import {
    Edit, Key, Globe, Folder, Plus, Trash2
} from 'lucide-react';

import {
    Button
} from 'shared/components/ui/button';
import {
    Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle
} from 'shared/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from 'shared/components/ui/dialog';
import WatsonApiKeysForm, {
    validateWatsonApiKeyForm
} from 'app/routing/organizations/configurations/WatsonApiKeysForm';
import AlertModal from 'shared/components/Alertmodal';
import {
    Context
} from 'shared/context/GlobalState';

const emptyFormData = {
    name: '',
    projectId: '',
    apiKey: '',
    url: '',
};

export default function ApiKeyManager({
    onSubmit,
    onDelete,
    apiKeys = [],
    loading = false,
}) {
    const [open, setOpen] = useState(false);
    const [editingKey, setEditingKey] = useState(null);
    const [deleteBtn, setDeleteBtn] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [deletedItem, setDeletedItem] = useState(null);
    const [errors, setErrors] = useState({});
    const {
        addNewNotifcation
    } = useContext(Context);

    // Form state
    const [formData, setFormData] = useState(emptyFormData);

    const resetForm = () => {
        setFormData(emptyFormData);
        setErrors({});
        setEditingKey(null);
    };

    const handleDialogOpenChange = (nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) {
            resetForm();
        }
    };

    const handleClearError = (field) => {
        setErrors((prev) => {
            if (!prev[field]) return prev;
            const next = {
                ...prev
            };
            delete next[field];
            return next;
        });
    };

    // Open dialog for adding a new key
    const handleAddKey = () => {
        resetForm();
        setOpen(true);
    };

    // Open dialog for editing an existing key
    const handleEditKey = (key) => {
        setFormData({
            name: key.name || '',
            projectId: key.projectId || '',
            apiKey: key.apiKey || '',
            url: key.url || '',
            _id: key._id,
        });
        setErrors({});
        setEditingKey(key);
        setOpen(true);
    };

    const handleShowDeleteModal = (id) => {
        setDeletedItem(id);
        setDeleteModal(true);
    };

    // Delete an API key
    const handleDeleteKey = async () => {
        setDeleteBtn(true);
        try {
            const updatedKeys = apiKeys.find((key) => key._id === deletedItem);
            await onDelete(updatedKeys);
            setDeleteModal(false);
        } catch (error) { /* empty */

        } finally {
            setDeleteBtn(false);
        }
        // setApiKeys(updatedKeys)
    };

    const handleSubmit = async () => {
        const validationErrors = validateWatsonApiKeyForm(formData);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        const payload = {
            ...formData,
            name: formData.name.trim(),
            apiKey: formData.apiKey.trim(),
            url: formData.url.trim(),
            projectId: formData.projectId.trim(),
        };

        try {
            await onSubmit(payload);
            setOpen(false);
            resetForm();
        } catch (error) {
            addNewNotifcation(error?.message || 'Something went wrong. Please try again.', 'danger');
        }
    };

    // Mask API key for display
    const maskApiKey = (key) => {
        if (!key) return '••••••••';
        return key.substring(0, 4) + '••••••••' + key.substring(key.length - 4);
    };

    return (
        <Card className="w-full">
            <CardHeader>
                <CardTitle>API Keys</CardTitle>
                <CardDescription>Manage your API keys for different services</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                {apiKeys.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                        <p>No API keys configured</p>
                        <p className="text-sm mt-1">Click the button below to add your first API key</p>
                    </div>
                ) : (
                    apiKeys.map((apiKey) => (
                        <Card key={apiKey._id} className="border border-muted">
                            <CardHeader className="p-4 pb-2">
                                <div className="flex justify-between items-start">
                                    <CardTitle className="text-lg">{apiKey.name}</CardTitle>
                                    <div className="flex gap-2">
                                        <Button variant="ghost" size="icon" onClick={() => handleEditKey(apiKey)} className="h-8 w-8">
                                            <Edit className="h-4 w-4" />
                                            <span className="sr-only">Edit</span>
                                        </Button>
                                        <Button
                                            disabled={deleteBtn}
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleShowDeleteModal(apiKey._id)}
                                            className="h-8 w-8 text-destructive hover:text-destructive"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                            <span className="sr-only">Delete</span>
                                        </Button>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="p-4 pt-0 space-y-3">
                                <div className="flex items-start gap-3">
                                    <Folder className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                                    <div className="space-y-1">
                                        <p className="text-sm font-medium">Project ID</p>
                                        <p className="text-sm text-muted-foreground break-all">{apiKey.projectId}</p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <Key className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                                    <div className="space-y-1">
                                        <p className="text-sm font-medium">API Key</p>
                                        <p className="text-sm text-muted-foreground font-mono">{maskApiKey(apiKey.apiKey)}</p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <Globe className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                                    <div className="space-y-1">
                                        <p className="text-sm font-medium">API URL</p>
                                        <p className="text-sm text-muted-foreground break-all">{apiKey.url}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))
                )}
            </CardContent>
            <CardFooter className="flex justify-end pb-6">
                {!apiKeys.length > 0 && (
                    <Button disabled={apiKeys.length > 0} onClick={handleAddKey} className="gap-2">
                        <Plus className="h-4 w-4" />
                        Add API Key
                    </Button>
                )}
            </CardFooter>

            {/* delete dialog alert */}
            <AlertModal
                openModal={deleteModal}
                setopenModal={setDeleteModal}
                onDelete={handleDeleteKey}
                loading={deleteBtn}
                title='Are you sure you want to delete this API key?'
                subTitle='This action cannot be undone and you will lose access to this API key.'
                btnTitle='Delete'
            />

            {/* Dialog for adding/editing API keys */}
            <Dialog open={open} onOpenChange={handleDialogOpenChange}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>{editingKey ? 'Edit API Key' : 'Add API Key'}</DialogTitle>
                        <DialogDescription>
                            {editingKey
                                ? 'Update your API key details below.'
                                : 'Enter your API key details to add a new connection.'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className='space-y-5'>
                        <WatsonApiKeysForm
                            formData={formData}
                            setFormData={setFormData}
                            errors={errors}
                            onClearError={handleClearError}
                        />
                        <div className='flex w-full justify-end gap-3 mt-4'>
                            <Button onClick={() => handleDialogOpenChange(false)} variant="outline">
                                Cancel
                            </Button>
                            <Button disabled={loading} onClick={handleSubmit}>
                                {loading ? 'Saving...' : 'Save'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </Card>
    );
}
